import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiRequest } from '../lib/utils';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const { user } = useAuth();
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            fetchCart();
        } else {
            setCart({ items: [], total: 0 });
        }
    }, [user]);

    const fetchCart = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const data = await apiRequest('/cart');
            setCart(data);
        } catch (error) {
            console.error('Failed to fetch cart:', error);
        } finally {
            setLoading(false);
        }
    };

    const addToCart = async (productId, quantity = 1) => {
        if (!user) {
            throw new Error('Vous devez être connecté pour ajouter au panier');
        }
        try {
            await apiRequest('/cart/add', {
                method: 'POST',
                body: JSON.stringify({ product_id: productId, quantity })
            });
            await fetchCart();
        } catch (error) {
            throw error;
        }
    };

    const updateCartItem = async (productId, quantity) => {
        try {
            await apiRequest('/cart/update', {
                method: 'PUT',
                body: JSON.stringify({ product_id: productId, quantity })
            });
            await fetchCart();
        } catch (error) {
            throw error;
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await apiRequest(`/cart/remove/${productId}`, {
                method: 'DELETE'
            });
            await fetchCart();
        } catch (error) {
            throw error;
        }
    };

    const clearCart = async () => {
        try {
            await apiRequest('/cart/clear', {
                method: 'DELETE'
            });
            setCart({ items: [], total: 0 });
        } catch (error) {
            throw error;
        }
    };

    const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{ 
            cart, 
            loading, 
            itemCount,
            addToCart, 
            updateCartItem, 
            removeFromCart, 
            clearCart,
            fetchCart 
        }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}
